import React, { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { submitFeedback, uploadScreenshot } from './feedback-api';
import { CONTACT_EMAIL, STEAM_URL } from '../../site-config.mjs';
import { trackEvent } from '../../analytics';
import '../../styles/page.css';
import './feedback.css';

const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024;
const ALLOWED_SCREENSHOT_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

const FEEDBACK_TYPES = [
  ['bug', 'Bug or unexpected behavior'],
  ['gameplay', 'Gameplay or balance'],
  ['performance', 'Performance or stability'],
  ['ui-accessibility', 'Interface or accessibility'],
  ['other', 'Something else'],
];

const EXPERIENCE_RATINGS = [
  ['1', '1, very poor'],
  ['2', '2, poor'],
  ['3', '3, okay'],
  ['4', '4, good'],
  ['5', '5, excellent'],
];

const FREQUENCIES = [
  ['once', 'Once'],
  ['sometimes', 'Sometimes'],
  ['often', 'Often'],
  ['always', 'Every time'],
  ['not-applicable', 'Not applicable'],
];

const readGameContext = (search) => {
  const params = new URLSearchParams(search);
  return {
    source: (params.get('source') || 'website').slice(0, 64),
    gameVersion: (params.get('version') || '').slice(0, 32),
    platform: (params.get('platform') || '').slice(0, 64),
  };
};

function ChoiceGroup({
  legend, hint, name, options, value, onChange, layout = 'stack', required = false,
}) {
  return (
    <fieldset className="field" aria-required={required || undefined}>
      <legend className="field__label">
        {legend}
        {!required && <span className="field__optional">Optional</span>}
      </legend>
      {hint && <p className="field__hint">{hint}</p>}
      <div className={`choices choices--${layout}`}>
        {options.map(([optionValue, label]) => (
          <label
            key={optionValue}
            className={`choice${value === optionValue ? ' is-selected' : ''}`}
          >
            <input
              type="radio"
              name={name}
              value={optionValue}
              checked={value === optionValue}
              onChange={(event) => onChange(event.target.value)}
            />
            <span className="choice__marker" aria-hidden="true" />
            <span className="choice__label">{label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export default function Feedback() {
  const { search } = useLocation();
  const [feedbackType, setFeedbackType] = useState('');
  const [experienceRating, setExperienceRating] = useState('');
  const [frequency, setFrequency] = useState('');
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [website, setWebsite] = useState('');
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInput = useRef(null);
  const gameContext = readGameContext(search);

  const resetForm = () => {
    setFeedbackType('');
    setExperienceRating('');
    setFrequency('');
    setMessage('');
    setScreenshot(null);
    setWebsite('');
    if (fileInput.current) fileInput.current.value = '';
  };

  const onScreenshotChanged = (event) => {
    const input = event.currentTarget;
    const [file] = input.files;
    setError('');

    if (!file) {
      setScreenshot(null);
      return;
    }
    if (!ALLOWED_SCREENSHOT_TYPES.has(file.type)) {
      setError('Screenshot must be a JPEG, PNG, or WebP image.');
      input.value = '';
      setScreenshot(null);
      return;
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      setError('Screenshot must be 5 MB or smaller.');
      input.value = '';
      setScreenshot(null);
      return;
    }

    setScreenshot(file);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setReceipt('');

    if (!feedbackType) {
      setError('Select the kind of feedback you are sending.');
      return;
    }

    setIsSubmitting(true);
    try {
      const screenshotKey = screenshot ? await uploadScreenshot(screenshot) : undefined;
      const result = await submitFeedback({
        feedbackType,
        experienceRating: experienceRating || undefined,
        frequency: frequency || undefined,
        message: message.trim() || undefined,
        screenshotKey,
        website,
        ...gameContext,
      });
      setReceipt(result.feedbackId);
      // The category only. Never the comment, the screenshot, or the reference,
      // which would put player-written content into a third party.
      trackEvent('feedback_submit', { feedback_type: feedbackType });
      resetForm();
    } catch (submissionError) {
      setError(submissionError.message || 'Feedback could not be sent. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page">
      <div className="shell feedback">
        <header className="page__head">
          <span className="eyebrow">Feedback</span>
          <h1 className="page__title">Send feedback</h1>
          <p className="page__sub">What happened, or what you&apos;d change.</p>

          {(gameContext.gameVersion || gameContext.platform) && (
            <div className="feedback__context">
              <span className="pill pill--ember">Warm</span>
              {gameContext.gameVersion && (
                <span className="pill">{`Version ${gameContext.gameVersion}`}</span>
              )}
              {gameContext.platform && <span className="pill">{gameContext.platform}</span>}
            </div>
          )}
        </header>

        <div className="page__body feedback__body">
          <form className="panel feedback__form" onSubmit={onSubmit} noValidate>
            {error && (
              <p className="notice notice--error" role="alert">
                {error}
              </p>
            )}

            {receipt && (
              <div className="notice notice--success" role="status">
                <strong>Thank you for the feedback.</strong>
                <span>{`Reference: ${receipt}`}</span>
              </div>
            )}

            <ChoiceGroup
              legend="What kind of feedback is this?"
              name="feedbackType"
              options={FEEDBACK_TYPES}
              value={feedbackType}
              onChange={setFeedbackType}
              required
            />

            <ChoiceGroup
              legend="How would you rate your experience?"
              name="experienceRating"
              options={EXPERIENCE_RATINGS}
              value={experienceRating}
              onChange={setExperienceRating}
              layout="grid"
            />

            <ChoiceGroup
              legend="How often does this happen?"
              name="frequency"
              options={FREQUENCIES}
              value={frequency}
              onChange={setFrequency}
              layout="grid"
            />

            <div className="field">
              <label className="field__label" htmlFor="feedback-message">
                Anything else you want us to know?
                <span className="field__optional">Optional</span>
              </label>
              <textarea
                id="feedback-message"
                className="input input--textarea"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                maxLength={4000}
                placeholder="What happened? What did you expect? Any ideas or suggestions?"
              />
              <p className="field__hint field__hint--end">{`${message.length}/4000 characters`}</p>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="feedback-screenshot">
                Screenshot
                <span className="field__optional">Optional</span>
              </label>
              <input
                id="feedback-screenshot"
                className="input input--file"
                ref={fileInput}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={onScreenshotChanged}
              />
              <p className="field__hint">
                JPEG, PNG, or WebP up to 5 MB. Avoid including personal or sensitive information.
              </p>
            </div>

            <div className="honeypot" aria-hidden="true">
              <label htmlFor="feedback-website">Website</label>
              <input
                id="feedback-website"
                name="website"
                value={website}
                onChange={(event) => setWebsite(event.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <button type="submit" className="btn btn--primary btn--lg" disabled={isSubmitting}>
              {isSubmitting && <span className="spinner" aria-hidden="true" />}
              {isSubmitting && (screenshot ? 'Uploading and sending' : 'Sending')}
              {!isSubmitting && 'Send feedback'}
            </button>
          </form>

          <aside className="feedback__aside">
            <div className="panel feedback__note">
              <h2>What makes a report useful</h2>
              <ul>
                <li>What happened, and what you expected instead.</li>
                <li>Where you were and what you were doing when it went wrong.</li>
                <li>Whether it happens every time or only sometimes.</li>
                <li>A screenshot, if the problem is something you can see.</li>
              </ul>
            </div>
            <div className="panel feedback__note">
              <h2>Other ways to reach us</h2>
              <p className="feedback__note-body">
                If you would rather not use the form, mail
                {' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                {' '}
                or post in the
                {' '}
                <a href={STEAM_URL} target="_blank" rel="noreferrer">
                  Steam discussions
                </a>
                . We read these.
              </p>
            </div>

            <p className="feedback__legal">
              Feedback is optional and anonymous. We do not ask for your name or email. See the
              {' '}
              <Link to="/privacy">Privacy Policy</Link>
              {' '}
              for how submissions are handled.
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
}
